import { MouseEvent } from 'react';

import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { GridColumnMenuItemProps, useGridRootProps } from '@mui/x-data-grid-premium';

import { ContextMenuUnsortIcon } from './GeneratikTable.icons';
import { useGeneratikTableContext } from './GeneratikTableContext';

/**
 * Same behaviour as MUI's default columnMenuSortItem, but the "Unsort" row
 * gets an icon — the default component hardcodes an empty ListItemIcon for
 * it, with no slot to override.
 */
const GeneratikTableColumnMenuSortItem = ({ colDef, onClick }: GridColumnMenuItemProps) => {
  const ctx = useGeneratikTableContext();
  const rootProps = useGridRootProps();
  const apiRef = ctx.apiRef;

  const currentDirection =
    ctx.sortActions.sortModel.find((item) => item.field === colDef.field)?.sort ?? null;
  const sortingOrder = colDef.sortingOrder ?? rootProps.sortingOrder;

  if (rootProps.disableColumnSorting || !colDef.sortable || !sortingOrder.some((item) => !!item)) {
    return null;
  }

  const handleClick = (direction: 'asc' | 'desc' | null) => (event: MouseEvent<HTMLLIElement>) => {
    apiRef.current.sortColumn(colDef.field, direction === currentDirection ? null : direction);
    onClick(event);
  };

  const getLabel = (key: string) => {
    const label = apiRef.current.getLocaleText(
      key as Parameters<typeof apiRef.current.getLocaleText>[0],
    );
    return typeof label === 'function' ? label(colDef) : label;
  };

  return (
    <>
      {sortingOrder.includes('asc') && currentDirection !== 'asc' && (
        <MenuItem onClick={handleClick('asc')}>
          <ListItemIcon>
            <rootProps.slots.columnMenuSortAscendingIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{getLabel('columnMenuSortAsc')}</ListItemText>
        </MenuItem>
      )}
      {sortingOrder.includes('desc') && currentDirection !== 'desc' && (
        <MenuItem onClick={handleClick('desc')}>
          <ListItemIcon>
            <rootProps.slots.columnMenuSortDescendingIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{getLabel('columnMenuSortDesc')}</ListItemText>
        </MenuItem>
      )}
      {sortingOrder.includes(null) && currentDirection != null && (
        <MenuItem onClick={handleClick(null)}>
          <ListItemIcon>
            <ContextMenuUnsortIcon />
          </ListItemIcon>
          <ListItemText>{apiRef.current.getLocaleText('columnMenuUnsort')}</ListItemText>
        </MenuItem>
      )}
    </>
  );
};

export default GeneratikTableColumnMenuSortItem;
